// The studio's logo (#498): what a stored one reads as, whether a picked file
// is opened at all, the encodings to try, and the words.
//
// The rules are `core/src/logo.rs`, whose header is the argument for keeping
// the logo on the studio document as a data URI rather than in Storage, and
// for the numbers. This file is the web's adapter; `src/lib/logoImage.js` is
// the browser half that decodes and encodes, and the phones do the same over
// the same calls.
import { coreSync } from './core.js';

const str = (v) => (v === null || v === undefined ? null : String(v));

/** The numbers: `{ maxChars, boxWidth, boxHeight, maxSourceBytes }`. */
export const logoPolicy = () => coreSync().logoPolicy();

/**
 * A stored logo as every surface reads it: the data URI when the core would
 * keep it, otherwise null — which the invoice and the report draw as no logo.
 */
export const normaliseLogo = (value) => coreSync().normaliseLogo(str(value)) ?? null;

/**
 * The name an issued invoice keeps its logo under (#622): the server's copy
 * at `invoiceLogos/{this}`, so a frozen invoice draws the logo it was sent
 * with. Null for no logo. `fingerprint` in core/src/logo.rs.
 */
export const logoFingerprint = (value) => coreSync().logoFingerprint(str(value)) ?? null;

/** `'png'` or `'jpeg'` for a data URI the core would keep, else null. */
export const logoKind = (uri) => coreSync().logoKind(String(uri ?? '')) ?? null;

/** Why a picked file is not opened, as a code, or null to decode it. */
export const logoSourceRefusal = (contentType, bytes) => (
  coreSync().logoSourceRefusal(str(contentType), Number(bytes) || 0) ?? null
);

/** A picture's size fitted into the box: `{ width, height }`. */
export const logoFit = (width, height) => coreSync().logoFit(Math.round(width), Math.round(height));

/** The encodings to try, in order: `[{ format, quality, width, height }]`. */
export const logoPlan = (width, height, sourceType) => (
  coreSync().logoPlan(Math.round(width), Math.round(height), str(sourceType))
);

/** A label or sentence of the logo field by code. */
export const logoMessage = (code) => coreSync().logoMessage(String(code)) ?? null;

// The field's words, by code, as literals. Every code the core answers is
// here; test/core/logo.wasm.test.js holds the two together.
export const LOGO_WORDING = Object.freeze({
  label: 'Logo',
  hint: 'This logo will appear on all business reports such as invoices.',
  choose: 'Choose Image',
  change: 'Change Image',
  remove: 'Remove',
  alt: 'Studio logo',
  none: 'No logo',
  working: 'Preparing the logo...',
  not_an_image: "That file isn't a picture a logo can be made from. Choose a PNG or JPEG image.",
  source_too_large: 'That picture is larger than 20 MB. Choose a smaller one.',
  unreadable: "That picture couldn't be opened. Try saving it as a PNG or JPEG first.",
  too_detailed: 'That picture is too detailed to keep as a logo, even made smaller. Try a simpler one.',
});
